import React, { useEffect, useMemo, useState } from "react";
import {
    Grid,
    Typography,
    Button,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Checkbox,
    FormControl,
    Chip,
    InputBase,
    Box,
} from "@mui/material";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import Autocomplete from "@mui/lab/Autocomplete";
import { useSelector, useDispatch } from "react-redux";
import { Alert, Card } from "reactstrap";
import { makeStyles } from "@mui/styles";
import SearchIcon from "@mui/icons-material/Search";

import CustomButton from "../../../src/components/CustomButton/CustomButton";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import Layout from "../../components/Wrappers/Layout";
import SOLogout from "../../components/Logout/SOLogout";
import regenerateToken from "../../components/RegenerateToken/regenerateToken";

import { getChargebackList } from "../../redux/actions/getchargebackAction";
import { updateChargeback, resetUpdateChargeback } from "../../redux/actions/updatechargebackAction";
import { getUserNameContactSuggestionList } from "../../redux/actions/partnerGetUserNameContactSuggestionAction";
import { getCustomerIdAndNameList } from "../../redux/actions/getCustomerIdAndNameListAction";
import getSymbolFromCurrency from "currency-symbol-map";

import Util from "../../validation/index";
import moment from "moment";

import success from "../../assets/img/success.png";
import failed from "../../assets/img/failed.png";
import pending from "../../assets/img/pending.png";
import proccessing from "../../assets/img/proccessing.png";

// ─── Constants ────────────────────────────────────────────────────────────────

const DATE_FORMAT = "YYYY-MM-DD";
const TABLE_COLUMNS = [
    "Transaction Date",
    "Transaction ID",
    "Order ID",
    "Captured Amount",
    "Status",
    "CB Amount",
    "CB Reason",
];
const NOTIFICATION_STATUSES = ["success", "failed", "pending", "proccessing", "processing"];
const STANDARD_INPUT_SX = {
    "& .MuiInput-underline:before": { borderBottomColor: "gray" },
    "& .MuiInput-underline:hover:not(.Mui-disabled):before": { borderBottomColor: "gray" },
    "& .MuiInput-underline:after": { borderBottomColor: "gray" },
};

const buildArray = (value) => [].concat(value || []);
const formatRequestDate = (value) => (value ? moment(value).format(DATE_FORMAT) : "");
const getMemberId = (value) => (value ? value.split("-")[0] : "");
const getMobile = (value) => (value ? value.split("-")[0].substring(1) : "");
const isValidAmount = (value) => /^\d*\.?\d*$/.test(value);

const getContactOptionLabel = (option) => {
    if (!option || typeof option === "string") return option || "";
    const { countryCode = "", mobile = "", customerMobileOrName = "" } = option;
    return mobile || customerMobileOrName
        ? `+${countryCode} ${mobile}-${customerMobileOrName}`
        : "";
};

const getCustomerNameOptionLabel = (option) => {
    if (!option || typeof option === "string") return option || "";
    return option.customerMobileOrName || "";
};

const filterContactOptions = (options, { inputValue }) => {
    if (inputValue.length < 3) return [];
    return options
        .filter(
            (option) =>
                option &&
                typeof option !== "string" &&
                ((option.mobile && option.mobile.includes(inputValue)) ||
                    (option.customerMobileOrName && option.customerMobileOrName.includes(inputValue)))
        )
        .slice(0, 20);
};

const filterCustomerNameOptions = (options, { inputValue }) => {
    if (inputValue.length < 3) return [];
    return options
        .filter(
            (option) =>
                option &&
                typeof option !== "string" &&
                option.customerMobileOrName &&
                option.customerMobileOrName.includes(inputValue)
        )
        .slice(0, 20);
};

const filterCustomerIdOptions = (options, { inputValue }) => {
    if (inputValue.length < 3) return [];
    return options.filter((option) => String(option || "").includes(inputValue)).slice(0, 20);
};

const useStyles = makeStyles({
    table: { minWidth: 1200 },
    headerCell: { fontWeight: "bold", color: "white", whiteSpace: "nowrap" },
    actionBar: {
        display: "flex",
        justifyContent: "center",
        gap: "12px",
        padding: "10px 0",
        backgroundColor: "transparent",
    },
});

// ─── Component ────────────────────────────────────────────────────────────────

const Chargeback = () => {
    const classes = useStyles();
    const dispatch = useDispatch();

    // ── Redux ──────────────────────────────────────────────────────────────────
    const login = useSelector((state) => state.login);
    const regenerateTokenRes = useSelector(({ regenerateToken }) => regenerateToken);
    const getChargebackListRes = useSelector(({ getChargebackList }) => getChargebackList);
    const updateChargebackRes = useSelector(({ updateChargeback }) => updateChargeback);
    const getUsernameContactSuggestionRes = useSelector(({ partnerGetUsernameContactSuggestionsLists }) => partnerGetUsernameContactSuggestionsLists);
    const getCustomerIdAndNameListRes = useSelector(({ getCustomerIdAndNameList }) => getCustomerIdAndNameList);

    const loginData = login.data || {};
    const tokenData = regenerateTokenRes.data || {};
    const chargebackListData = getChargebackListRes.data || {};
    const updateChargebackData = updateChargebackRes.data || {};
    const usernameSuggestionData = getUsernameContactSuggestionRes.data || {};
    const customerIdListData = getCustomerIdAndNameListRes.data || {};

    const authToken = tokenData.AuthToken;
    const walletOwnerIdValue = loginData.subRole === "" ? loginData.partnerId : loginData.parentId;
    const userSuggestionOptions = useMemo(
        () => buildArray(usernameSuggestionData.list),
        [usernameSuggestionData.list]
    );
    const customerIdOptions = useMemo(
        () => buildArray(customerIdListData.currencyList),
        [customerIdListData.currencyList]
    );
    const totalRecordCount = Number(chargebackListData.count) || 0;

    // ── Filter state  (identical to Refund.jsx) ────────────────────────────────
    const [selectedDate, setSelectedDate] = useState(() => {
        const today = new Date();
        return new Date(today.getFullYear(), today.getMonth(), 1);
    });
    const [selectedToDate, setSelectedToDate] = useState(() => new Date());
    const [customerOrMobName, setCustomerOrMobName] = useState("");
    const [toCustName, setToCustName] = useState("");
    const [memberId, setMemberId] = useState("");

    const [user, setUser] = useState({ getOrderId: "" });
    const { getOrderId } = user;

    const [transactionIds, setTransactionIds] = useState([]);
    const [transactionIdInput, setTransactionIdInput] = useState("");

    // ── Pagination ─────────────────────────────────────────────────────────────
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [pageNo, setPageNo] = useState(0);
    const isSearchRef = React.useRef(false);

    // ── Table state ────────────────────────────────────────────────────────────
    const [isLoading, setIsLoading] = useState(false);
    const [hasFetched, setHasFetched] = useState(false);
    const [chargebackRows, setChargebackRows] = useState([]);

    // ── Per-row editable fields: { [index]: { cbAmount, cbReason, cbDate, isRetrival, blacklist } }
    const [rowEdits, setRowEdits] = useState({});
    const [selectedRows, setSelectedRows] = useState({});
    const selectAll = chargebackRows.length > 0 && chargebackRows.every((row) => selectedRows[row.id]);
    const hasSelectedRows = useMemo(() => Object.values(selectedRows).some(Boolean), [selectedRows]);
    const [searchTriggered, setSearchTriggered] = useState(false);

    // ── Dialogs ────────────────────────────────────────────────────────────────
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [notificationOpen, setNotificationOpen] = useState(false);
    const [notificationData, setNotificationData] = useState({});
    const notificationStatus = (notificationData.status || "").toLowerCase();
    const shouldShowNotification = NOTIFICATION_STATUSES.includes(notificationStatus);



    const handleTransactionIdKeyDown = (e) => {
        const val = transactionIdInput.trim();
        if ((e.key === "Enter" || e.key === ",") && val) {
            e.preventDefault();
            if (!transactionIds.includes(val)) {
                setTransactionIds((prev) => [...prev, val]);
            }
            setTransactionIdInput("");
        }
        if (e.key === "Backspace" && !transactionIdInput && transactionIds.length) {
            setTransactionIds((prev) => prev.slice(0, -1));
        }
    };

    const removeTransactionId = (id) =>
        setTransactionIds((prev) => prev.filter((t) => t !== id));

    // ─── Filter helpers (identical to Refund.jsx) ─────────────────────────────

    const handleDateChange = (d) => setSelectedDate(d);
    const handleToDateChange = (d) => setSelectedToDate(d);

    const handleChange = ({ target: { name, value } }) => {
        if (name === "customerOrMobName") setCustomerOrMobName(value);
        if (name === "toCustName") setToCustName(value);
        if (name === "memberId") setMemberId(value);
    };

    const onInputChange = ({ target: { name, value } }, type) => {
        setUser((prev) => {
            const isValid =
                type === "number"
                    ? Util.verifyMobile(value)
                    : type === "both"
                        ? Util.checkWhiteSpace(value)
                        : Util.noWhiteSpace(value);

            return isValid ? { ...prev, [name]: value } : prev;
        });
    };

    // Fetch suggestion lists on token ready (same as Refund.jsx)
    useEffect(() => {
        if (tokenData.status === "success") {
            dispatch(
                getUserNameContactSuggestionList(
                    {
                        actionCustomerMasterRequestVO: {
                            customerMobileOrName: customerOrMobName,
                            walletOwnerId: walletOwnerIdValue,
                        },
                    },
                    authToken
                )
            );
        }
    }, [tokenData.status]); // eslint-disable-line

    useEffect(() => {
        if (tokenData.status === "success") {
            dispatch(
                getCustomerIdAndNameList(
                    {
                        loginDetails: { walletOwnerId: walletOwnerIdValue },
                        actionCustomerMasterRequestVO: { customerNameOrId: "" },
                    },
                    authToken
                )
            );
        }
    }, [tokenData.status]); // eslint-disable-line

    // ─── Row-edit helpers ─────────────────────────────────────────────────────

    const getRowEdit = (id, field, fallback = "") =>
        rowEdits[id]?.[field] ?? fallback;

    const setRowEdit = (id, field, value) =>
        setRowEdits((prev) => {
            const currentRowEdit = prev[id] || {};
            if (currentRowEdit[field] === value) return prev;

            return {
                ...prev,
                [id]: { ...currentRowEdit, [field]: value },
            };
        });

    const initRowEdits = (rows) => {
        setRowEdits((prev) => {
            const next = { ...prev };
            rows.forEach((row) => {
                if (!next[row.id]) {                          // don't overwrite existing edits
                    next[row.id] = {
                        cbAmount: row.cbAmount ?? row.amount ?? "",
                        cbReason: row.cbReason ?? "",
                        cbDate: row.cbDate ?? "",
                        isRetrival: row.isRetrivalRequest ?? "N",
                        blacklist: row.blacklistDetails ?? "N",
                    };
                }
            });
            return next;
        });
        // ← removed: setSelectedRows({})  so selections persist across pages
    };

    // ─── Fetch chargeback list ────────────────────────────────────────────────

    const getChargebackListReq = (pageOverride,includeTransactionIds = searchTriggered) => {
        setIsLoading(true);
        setHasFetched(true);
        const currentPage = pageOverride ?? pageNo;
        const req = {
            actionCustomerMasterRequestVO: {          // ← match Refund.jsx wrapper key
                walletOwnerId: walletOwnerIdValue,
                transactionIds: includeTransactionIds && transactionIds.length > 0 ? transactionIds : [],
                customerId: getMemberId(memberId),
                orderDescription: getOrderId,
                fromDate: formatRequestDate(selectedDate),
                toDate: formatRequestDate(selectedToDate),
                mobile: getMobile(customerOrMobName),
                name: toCustName,
                page: currentPage + 1,
                record: rowsPerPage,
            },
        };
        dispatch(getChargebackList(req, authToken));
    };

    const handleSearch = () => {
          const shouldInclude = transactionIds.length > 0;
    setSearchTriggered(shouldInclude);
        if (pageNo === 0) {
            getChargebackListReq(0,shouldInclude);
        } else {
            setPageNo(0); // triggers useEffect below, which fetches page 1
        }
    };

    // useEffect(() => {
    //     getChargebackListReq();
    // }, [pageNo, rowsPerPage]); // eslint-disable-line

    useEffect(() => {
        if (tokenData.status === "success") {
            getChargebackListReq(undefined, searchTriggered);
        }
    }, [pageNo, rowsPerPage, tokenData.status]); // eslint-disable-line

    useEffect(() => {
        if (!hasFetched) return;
        const data = chargebackListData;

        if (data.status === "success") {
            setIsLoading(false);
            // ↓ was: data.chargebackList — wrong key
            const list = [].concat(data.customerMasterDetailsRequestVOList || []);
            setChargebackRows(list);
            initRowEdits(list);
        }
        if (data.status === "failed" || data.status === "Not Found") {
            setIsLoading(false);
            setChargebackRows([]);
            handleAuthErrors(data);
        }
    }, [getChargebackListRes]); // eslint-disable-line

    // ─── Auth helpers ─────────────────────────────────────────────────────────

    const handleAuthErrors = (data) => {
        if (data.description === "session out")
            SOLogout(dispatch, authToken, tokenData.partnerId);
        if (data.description === "Authorization failed")
            regenerateToken(dispatch, authToken, tokenData.partnerId);
    };

    // ─── Pagination ───────────────────────────────────────────────────────────

    const handleChangePage = (_, newPage) => setPageNo(newPage);

    const handleChangeRowsPerPage = (event) => {
        const newRows = parseInt(event.target.value, 10);
        const firstIndex = pageNo * rowsPerPage;
        setRowsPerPage(newRows);
        setPageNo(Math.floor(firstIndex / newRows));
    };

    // ─── Row selection ────────────────────────────────────────────────────────

    const handleSelectAll = (e) => {
        const checked = e.target.checked;
        if (!checked) {
            // only deselect rows on current page
            setSelectedRows((prev) => {
                const next = { ...prev };
                chargebackRows.forEach((row) => delete next[row.id]);
                return next;
            });
            return;
        }

        setSelectedRows((prev) => {
            const next = { ...prev };
            chargebackRows.forEach((row) => { next[row.id] = true; });
            return next;
        });
    };

    const handleSelectRow = (id) => (e) => {
        setSelectedRows((prev) => {
            const next = { ...prev };
            if (e.target.checked) {
                next[id] = true;
            } else {
                delete next[id];
            }
            return next;
        });
    };

    // ─── Confirm & submit ─────────────────────────────────────────────────────

    const openConfirm = () => {
        if (!hasSelectedRows) return;
        setConfirmOpen(true);
    };

    const handleConfirmClose = () => setConfirmOpen(false);

    const handleConfirmOk = () => {
        setConfirmOpen(false);

        const chargebacks = chargebackRows.reduce((acc, row) => {
            if (!selectedRows[row.id]) return acc;
            acc.push({
                transactionId: row.id,
                amount: Number(getRowEdit(row.id, "cbAmount")) || 0,
                reason: getRowEdit(row.id, "cbReason", ""),
            });
            return acc;
        }, []);

        if (!chargebacks.length) return;

        const req = {
            actionCustomerMasterRequestVO: {
                walletOwnerId: walletOwnerIdValue,
                initiatedBy: "Partner",
                chargebacks,
            },
        };
        // console.log("Chargeback Request:", req);
        dispatch(updateChargeback(req, authToken));
    };

    useEffect(() => {
        const data = updateChargebackData;
        if (!data.status) return;
        setNotificationData(data);
        setNotificationOpen(true);
        if (data.status === "success" || data.status === "Success") getChargebackListReq();
        handleAuthErrors(data);
        dispatch(resetUpdateChargeback());
    }, [updateChargebackRes]); // eslint-disable-line

    const handleNotificationClose = () => setNotificationOpen(false);

    const notifIcon = () => {
        if (notificationStatus === "success") return success;
        if (notificationStatus === "pending") return pending;
        if (notificationStatus === "proccessing" || notificationStatus === "processing") return proccessing;
        return failed;
    };

    // ─── Render ───────────────────────────────────────────────────────────────

    return (
        <>
            <Layout>

                {/* ════════════════════════════════════════════════════════════
                    FILTER SECTION  —  kept exactly as in Refund.jsx
                ════════════════════════════════════════════════════════════ */}
                <Card>
                    <Grid container spacing={2} style={{ padding: "25px", marginTop: "2rem" }}>

                        {/* From Date */}
                        <Grid item xs={12} sm={12} md={3} lg={3}>
                            <FormControl fullWidth>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        label="From Date"
                                        format="MM/dd/yyyy"
                                        value={selectedDate}
                                        onChange={handleDateChange}
                                        disableFuture={true}
                                    />
                                </LocalizationProvider>
                            </FormControl>
                        </Grid>

                        {/* To Date */}
                        <Grid item xs={12} sm={12} md={3} lg={3}>
                            <FormControl fullWidth>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        label="To Date"
                                        format="MM/dd/yyyy"
                                        value={selectedToDate}
                                        onChange={handleToDateChange}
                                        disableFuture={true}
                                    />
                                </LocalizationProvider>
                            </FormControl>
                        </Grid>

                        {/* Transaction Id */}
                        {/* <Grid item xs={12} sm={12} md={3} lg={3}>
                            <TextField
                                autoFocus
                                margin="dense"
                                className="removeArrows"
                                id="name"
                                label="Transaction Id"
                                type="number"
                                fullWidth
                                name="getTrackingId"
                                value={getTrackingId}
                                onChange={(e) => onInputChange(e, "number")}
                                style={{ marginTop: "3px" }}
                                variant="standard"
                                InputProps={{
                                    sx: {
                                        "&:before": { borderBottomColor: "gray" },
                                        "&:after": { borderBottomColor: "gray" },
                                    },
                                }}
                                InputLabelProps={{ sx: { color: "gray" } }}
                            />
                        </Grid> */}
                        {/* Transaction IDs — multi-value chip input */}
                        {/* Transaction IDs — multi-value */}
                        <Grid item xs={12} sm={12} md={3} lg={3}>
                            <Autocomplete
                                multiple
                                freeSolo
                                options={[]}
                                value={transactionIds}
                                inputValue={transactionIdInput}
                                onInputChange={(_, newValue) => {
                                    // only allow digits
                                    if (newValue === "" || /^\d+$/.test(newValue))
                                        setTransactionIdInput(newValue);
                                }}
                                onChange={(_, newValue) => setTransactionIds(newValue)}
                                onKeyDown={(e) => {
                                    const val = transactionIdInput.trim();
                                    if ((e.key === "Enter" || e.key === ",") && val) {
                                        e.preventDefault();
                                        if (!transactionIds.includes(val)) {
                                            setTransactionIds((prev) => [...prev, val]);
                                        }
                                        setTransactionIdInput("");
                                    }
                                    if (e.key === "Backspace" && !transactionIdInput && transactionIds.length) {
                                        setTransactionIds((prev) => prev.slice(0, -1));
                                    }
                                }}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip
                                            size="small"
                                            label={option}
                                            {...getTagProps({ index })}
                                            sx={{ height: "20px", fontSize: "11px" }}
                                        />
                                    ))
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Transaction Id(s)"
                                        variant="standard"
                                        margin="dense"
                                        style={{ marginTop: "3px" }}
                                        InputProps={{
                                            ...params.InputProps,
                                            sx: {
                                                "&:before": { borderBottomColor: "gray" },
                                                "&:after": { borderBottomColor: "gray" },
                                            },
                                        }}
                                        InputLabelProps={{ sx: { color: "gray" } }}
                                    />
                                )}
                            />
                        </Grid>

                        {/* Member ID */}
                        <Grid item xs={12} sm={12} md={3} lg={3}>
                            <Autocomplete
                                id="combo-box-demo"
                                options={customerIdOptions}
                                getOptionLabel={(option) => option}
                                filterOptions={filterCustomerIdOptions}
                                value={memberId}
                                onChange={(_, value) => setMemberId(value || "")}
                                onInputChange={(_, newInputValue) => setMemberId(newInputValue)}
                                renderInput={(params) => (
                                    <TextField
                                        autoFocus
                                        margin="dense"
                                        id="name"
                                        label="Member ID"
                                        style={{ marginTop: "3px" }}
                                        type="tel"
                                        fullWidth
                                        {...params}
                                        name="memberId"
                                        variant="standard"
                                        sx={STANDARD_INPUT_SX}
                                    />
                                )}
                            />
                        </Grid>

                        {/* Order Id */}
                        <Grid item xs={12} sm={12} md={3} lg={3}>
                            <TextField
                                autoFocus
                                margin="dense"
                                id="name"
                                label="Order Id"
                                fullWidth
                                name="getOrderId"
                                value={getOrderId}
                                variant="standard"
                                onChange={(e) => onInputChange(e, "both")}
                                style={{ marginTop: "3px" }}
                                InputProps={{
                                    sx: {
                                        "&:before": { borderBottomColor: "gray" },
                                        "&:after": { borderBottomColor: "gray" },
                                    },
                                }}
                                InputLabelProps={{ sx: { color: "gray" } }}
                            />
                        </Grid>

                        {/* Contact */}
                        <Grid item xs={12} sm={12} md={3} lg={3}>
                            <Autocomplete
                                id="combo-box-demo"
                                options={userSuggestionOptions}
                                getOptionLabel={getContactOptionLabel}
                                filterOptions={filterContactOptions}
                                renderInput={(params) => (
                                    <TextField
                                        autoFocus
                                        margin="dense"
                                        id="name"
                                        label="Contact"
                                        style={{ marginTop: "3px" }}
                                        type="text"
                                        fullWidth
                                        {...params}
                                        value={customerOrMobName || ""}
                                        name="customerOrMobName"
                                        onBlur={handleChange}
                                        variant="standard"
                                        sx={STANDARD_INPUT_SX}
                                    />
                                )}
                            />
                        </Grid>

                        {/* Beneficiary Name */}
                        <Grid item xs={12} sm={12} md={3} lg={3}>
                            <Autocomplete
                                id="combo-box-demo"
                                options={userSuggestionOptions}
                                getOptionLabel={getCustomerNameOptionLabel}
                                filterOptions={filterCustomerNameOptions}
                                renderInput={(params) => (
                                    <TextField
                                        autoFocus
                                        margin="dense"
                                        id="name"
                                        label="Beneficiary Name"
                                        style={{ marginTop: "3px" }}
                                        type="text"
                                        fullWidth
                                        {...params}
                                        value={toCustName || ""}
                                        name="toCustName"
                                        onBlur={handleChange}
                                        variant="standard"
                                        sx={STANDARD_INPUT_SX}
                                    />
                                )}
                            />
                        </Grid>

                    </Grid>

                    {/* Search button */}
                    <Grid
                        container
                        style={{ padding: "10px", marginTop: "1.5rem", marginBottom: "15px" }}
                    >
                        <Grid item md={5}>
                            <CustomButton
                                style={{ padding: "8px", marginLeft: "15px" }}
                                onClick={handleSearch}
                            >
                                Search <SearchIcon />
                            </CustomButton>
                        </Grid>
                    </Grid>
                </Card>

                {/* ════════════════════════════════════════════════════════════
                    REPORT / TABLE SECTION  —  Chargeback-specific
                ════════════════════════════════════════════════════════════ */}
                {isLoading ? (
                    <LoadingSpinner />
                ) : !hasFetched || chargebackRows.length === 0 ? (
                    <Card>
                        <Grid container style={{ padding: "10px" }}>
                            <Grid item xs={12}>
                                <Typography variant="body1" style={{ fontWeight: "700" }} className="themeText">
                                    Common Chargeback
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Alert className="my-3 tableHeaderColor" variant="secondary" style={{ color: "white" }}>
                                    No Records Found!
                                </Alert>
                            </Grid>
                        </Grid>
                    </Card>
                ) : (
                    <Card>
                        <Grid container style={{ padding: "10px" }}>
                            <Grid item xs={12}>
                                <Typography variant="body1" style={{ fontWeight: "700" }} className="themeText">
                                    Common Chargeback
                                </Typography>
                            </Grid>

                            <TableContainer component={Paper} className="my-3" style={{ overflowX: "auto" }}>
                                <Table className={classes.table}>

                                    {/* ── Table head ── */}
                                    <TableHead>
                                        <TableRow className="tableHeaderColor">
                                            <TableCell align="center" style={{ color: "white" }}>
                                                <Checkbox
                                                    checked={selectAll}
                                                    onChange={handleSelectAll}
                                                    sx={{ color: "white", "&.Mui-checked": { color: "white" } }}
                                                />
                                            </TableCell>
                                            {TABLE_COLUMNS.map((col) => (
                                                <TableCell
                                                    key={col}
                                                    align="center"
                                                    className={classes.headerCell}
                                                >
                                                    {col}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>

                                    {/* ── Table body ── */}
                                    <TableBody>
                                        {chargebackRows.map((row) => (
                                            <TableRow key={row.id}>

                                                {/* Checkbox */}
                                                <TableCell align="center">
                                                    <Checkbox
                                                        checked={!!selectedRows[row.id]}
                                                        onChange={handleSelectRow(row.id)}
                                                    />
                                                </TableCell>

                                                {/* Read-only columns */}

                                                <TableCell align="center">{row.registeredTime}</TableCell>
                                                <TableCell align="center">{row.id}</TableCell>
                                                <TableCell align="center">{row.orderDescription}</TableCell>
                                                <TableCell align="center">  {" "}
                                                    {getSymbolFromCurrency(row.currency) +
                                                        " " + row.amount}</TableCell>
                                                <TableCell align="center">{row.status}</TableCell>


                                                {/* Editable: CB Amount */}
                                                <TableCell align="center">
                                                    <TextField
                                                        className="removeArrows"
                                                        size="small"
                                                        type="text"
                                                        value={getRowEdit(row.id, "cbAmount")}
                                                        onChange={(e) => {
                                                            const val = e.target.value;
                                                            if (isValidAmount(val))
                                                                setRowEdit(row.id, "cbAmount", val);
                                                        }}
                                                        inputProps={{ style: { width: "80px" } }}
                                                        variant="outlined"
                                                    />
                                                </TableCell>

                                                {/* Editable: CB Reason */}
                                                <TableCell align="center">
                                                    <TextField
                                                        size="small"
                                                        type="text"
                                                        value={getRowEdit(row.id, "cbReason", "")}
                                                        onChange={(e) => setRowEdit(row.id, "cbReason", e.target.value)}
                                                        inputProps={{ style: { width: "160px" } }}
                                                        variant="outlined"
                                                        placeholder="Enter reason"
                                                    />
                                                </TableCell>

                                                {/* Editable: CB Date */}
                                                {/* <TableCell align="center">
                                                    <TextField
                                                        type="date"
                                                        size="small"
                                                        value={getRowEdit(index, "cbDate")}
                                                        onChange={(e) => setRowEdit(index, "cbDate", e.target.value)}
                                                        variant="outlined"
                                                        InputLabelProps={{ shrink: true }}
                                                        inputProps={{ style: { width: "130px" } }}
                                                    />
                                                </TableCell> */}


                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>

                                <div className="chargebackbuttonmain">

                                    {/* ── Action bar ── */}
                                    <div className={classes.actionBar}>
                                        <CustomButton
                                            style={{ padding: "8px 16px" }}
                                            onClick={openConfirm}
                                        >
                                            Approve Chargeback
                                        </CustomButton>
                                    </div>

                                    <div className={classes.actionBar}>
                                        {hasSelectedRows && (
                                            <CustomButton
                                                style={{ padding: "8px 16px", backgroundColor: "#aaa" }}
                                                onClick={() => setSelectedRows({})}
                                            >
                                                Clear Selection ({Object.values(selectedRows).filter(Boolean).length})
                                            </CustomButton>
                                        )}
                                        <CustomButton
                                            style={{ padding: "8px 16px" }}
                                            onClick={openConfirm}
                                        >
                                            Chargeback Selected ({Object.values(selectedRows).filter(Boolean).length})
                                        </CustomButton>
                                    </div>
                                </div>

                                <TablePagination
                                    rowsPerPageOptions={[5, 10, 25]}
                                    component="div"
                                    count={totalRecordCount}
                                    rowsPerPage={rowsPerPage}
                                    page={pageNo}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                    slotProps={{
                                        actions: {
                                            nextButton: {
                                                disabled: chargebackRows.length < rowsPerPage,
                                            },
                                        },
                                    }}
                                />
                            </TableContainer>
                        </Grid>
                    </Card>
                )}

                {/* ── Confirmation dialog ── */}
                <Dialog open={confirmOpen} onClose={handleConfirmClose}>
                    <DialogTitle style={{ width: "400px" }}>
                        Confirm Chargeback
                    </DialogTitle>
                    <DialogContent>
                        <Typography>
                            Do you really want to chargeback all selected transaction.
                        </Typography>
                    </DialogContent>
                    <DialogActions style={{ justifyContent: "center", paddingBottom: "16px" }}>
                        <CustomButton
                            style={{ padding: "8px 24px", backgroundColor: "#7b5c3e", color: "white", marginRight: "8px" }}
                            onClick={handleConfirmOk}
                        >
                            Yes
                        </CustomButton>
                        <CustomButton style={{ padding: "8px 24px" }} onClick={handleConfirmClose}>
                            Cancel
                        </CustomButton>
                    </DialogActions>
                </Dialog>

            </Layout>

            {/* ── Notification dialogs (mirrors Refund.jsx) ── */}
            {(notificationData.status === "success" || notificationData.status === "Success") && (
                <Dialog open={notificationOpen} onClose={handleNotificationClose}>
                    <DialogContent className="text-center" style={{ backgroundColor: "#00b9f51c" }}>
                        <img src={success} alt="" width="100px" className="my-2" />
                    </DialogContent>
                    <DialogContent>
                        <h4 style={{ margin: "0", textAlign: "center", fontSize: "15px" }}>
                            {notificationData.description}
                        </h4>
                    </DialogContent>
                    <DialogActions style={{ justifyContent: "center" }}>
                        <Button
                            style={{ margin: "10px 0", backgroundColor: "#00b9f5", color: "white" }}
                            variant="contained"
                            onClick={handleNotificationClose}
                        >
                            Ok
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
            {(notificationData.status === "Failed" || notificationData.status === "failed") && (
                <Dialog open={notificationOpen} onClose={handleNotificationClose}>
                    <DialogContent className="text-center" style={{ backgroundColor: "#00b9f51c" }}>
                        <img src={failed} alt="" width="100px" className="my-2" />
                    </DialogContent>
                    <DialogContent>
                        <h4 style={{ margin: "0", textAlign: "center", fontSize: "15px" }}>
                            {notificationData.description}
                        </h4>
                    </DialogContent>
                    <DialogActions style={{ justifyContent: "center" }}>
                        <Button
                            style={{ margin: "10px 0", backgroundColor: "#00b9f5", color: "white" }}
                            variant="contained"
                            onClick={handleNotificationClose}
                        >
                            Ok
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
            {notificationData.status === "Pending" && (
                <Dialog open={notificationOpen} onClose={handleNotificationClose}>
                    <DialogContent className="text-center" style={{ backgroundColor: "#00b9f51c" }}>
                        <img src={pending} alt="" width="100px" className="my-2" />
                    </DialogContent>
                    <DialogContent>
                        <h4 style={{ margin: "0", textAlign: "center", fontSize: "15px" }}>
                            {notificationData.description}
                        </h4>
                    </DialogContent>
                    <DialogActions style={{ justifyContent: "center" }}>
                        <Button
                            style={{ margin: "10px 0", backgroundColor: "#00b9f5", color: "white" }}
                            variant="contained"
                            onClick={handleNotificationClose}
                        >
                            Ok
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
            {notificationData.status === "proccessing" && (
                <Dialog open={notificationOpen} onClose={handleNotificationClose}>
                    <DialogContent className="text-center" style={{ backgroundColor: "#00b9f51c" }}>
                        <img src={proccessing} alt="" width="100px" className="my-2" />
                    </DialogContent>
                    <DialogContent>
                        <h4 style={{ margin: "0", textAlign: "center", fontSize: "15px" }}>
                            {notificationData.description}
                        </h4>
                    </DialogContent>
                    <DialogActions style={{ justifyContent: "center" }}>
                        <Button
                            style={{ margin: "10px 0", backgroundColor: "#00b9f5", color: "white" }}
                            variant="contained"
                            onClick={handleNotificationClose}
                        >
                            Ok
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </>
    );
};

export default Chargeback;
